import React, { useEffect, useMemo, useState } from 'react';
import {
  Send, Sparkles, MessageCircleReply, CheckCircle2, Calendar, Eye, Trophy,
  Loader2, Clock, AlertCircle, RotateCcw, CalendarX, ChevronDown,
  Play, Pause,
} from 'lucide-react';
import {
  collection, query, where, getDocs, orderBy, limit,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useCompany } from '../../../../context/CompanyContext';

/**
 * Lead Activity Timeline — revenue-focused chronological view.
 *
 * Shows how each lead moved from quote → follow-up → reply → won, so the
 * operator can answer at a glance: "Did automation help close this deal?"
 *
 * Data sources (all reused, no new infra):
 *   • quotes              → "Quote sent" + "Quote viewed" + "Quote approved"
 *   • scheduled_actions   → "Auto follow-up sent" (kind=quote_followup, status=executed)
 *   • conversations/msgs  → "Client replied" (inbound SMS)
 *   • tasks               → "Job scheduled" + "Job completed"
 */

// ───────────────────────────────────────────────────────────────────────────

const safeDate = (v) => {
  if (!v) return null;
  try {
    if (v?.toDate) return v.toDate();
    if (v?.seconds) return new Date(v.seconds * 1000);
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
};

const formatTime = (d) => d?.toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) || '';

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// ── Visual config per event kind ─────────────────────────────────────────────

const KIND = {
  quote_sent:       { icon: Send,               tone: 'orange',  label: 'Quote sent',          actor: 'You' },
  quote_viewed:     { icon: Eye,                tone: 'blue',    label: 'Quote viewed',        actor: 'Customer' },
  followup_sent:    { icon: Sparkles,           tone: 'violet',  label: 'Auto follow-up sent', actor: 'Automation' },
  followup_skipped: { icon: AlertCircle,        tone: 'gray',    label: 'Follow-up skipped',   actor: 'Automation' },
  client_reply:     { icon: MessageCircleReply, tone: 'sky',     label: 'Customer replied',    actor: 'Customer' },
  quote_approved:   { icon: CheckCircle2,       tone: 'emerald', label: 'Quote approved',      actor: 'Customer' },
  job_scheduled:    { icon: Calendar,           tone: 'indigo',  label: 'Job scheduled',       actor: 'You' },
  job_rescheduled:  { icon: RotateCcw,          tone: 'amber',   label: 'Visit rescheduled',   actor: 'Office' },
  job_unscheduled:  { icon: CalendarX,          tone: 'gray',    label: 'Visit unscheduled',   actor: 'Office' },
  job_completed:    { icon: Trophy,             tone: 'green',   label: 'Job completed — won', actor: 'You' },
  program_marked_active:   { icon: Play,  tone: 'emerald', label: 'Program marked Active',   actor: 'Office' },
  program_marked_inactive: { icon: Pause, tone: 'gray',    label: 'Program marked Inactive', actor: 'Office' },
};

const TONE_CLASSES = {
  orange:  { bg: 'bg-orange-50',  ring: 'ring-orange-100',  text: 'text-orange-700',  dot: 'bg-orange-400' },
  blue:    { bg: 'bg-blue-50',    ring: 'ring-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-400' },
  violet:  { bg: 'bg-violet-50',  ring: 'ring-violet-100',  text: 'text-violet-700',  dot: 'bg-violet-400' },
  gray:    { bg: 'bg-gray-50',    ring: 'ring-gray-200',    text: 'text-gray-500',    dot: 'bg-gray-300' },
  sky:     { bg: 'bg-sky-50',     ring: 'ring-sky-100',     text: 'text-sky-700',     dot: 'bg-sky-400' },
  emerald: { bg: 'bg-emerald-50', ring: 'ring-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  indigo:  { bg: 'bg-indigo-50',  ring: 'ring-indigo-100',  text: 'text-indigo-700',  dot: 'bg-indigo-400' },
  green:   { bg: 'bg-green-50',   ring: 'ring-green-100',   text: 'text-green-700',   dot: 'bg-green-400' },
  amber:   { bg: 'bg-amber-50',   ring: 'ring-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-400' },
};

// Human-readable label for the reason enum captured in tasks[].history[].
const RESCHEDULE_REASON_LABEL = {
  customer_request: 'Customer request',
  weather:          'Weather',
  technician:       'Technician',
  office:           'Office',
  system:           'System',
};

const formatReschedDate = (iso) => {
  if (!iso) return 'unscheduled';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // Date-only ISO (YYYY-MM-DD): render without time.
    if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

// ── Event collection helpers ─────────────────────────────────────────────────

async function loadQuoteEvents(companyId, leadId) {
  const events = [];
  const quoteIds = [];
  const qs = await getDocs(query(
    collection(db, 'companies', companyId, 'quotes'),
    where('leadId', '==', leadId),
  ));
  qs.forEach((d) => {
    const data = d.data() || {};
    const amount = data.totals?.grandTotal ?? data.total ?? 0;
    quoteIds.push(d.id);

    const sentAt = safeDate(data.sentAt) || safeDate(data.createdAt);
    if (sentAt) {
      events.push({
        id: `q-sent-${d.id}`,
        kind: 'quote_sent',
        when: sentAt,
        title: `Quote ${fmtMoney(amount)}`,
        detail: `Sent to ${data.client?.name || 'customer'}`,
        link: `/quotes/${d.id}`,
      });
    }
    const viewedAt = safeDate(data.viewedAt);
    if (viewedAt) {
      events.push({
        id: `q-viewed-${d.id}`,
        kind: 'quote_viewed',
        when: viewedAt,
        title: 'Customer opened the quote',
        detail: `Quote ${fmtMoney(amount)}`,
      });
    }
    const approvedAt = safeDate(data.approvedAt);
    if (approvedAt) {
      events.push({
        id: `q-approved-${d.id}`,
        kind: 'quote_approved',
        when: approvedAt,
        title: `Quote approved — ${fmtMoney(amount)}`,
        detail: 'Deal won 🎉',
        won: true,
      });
    }
  });
  return { events, quoteIds };
}

async function loadFollowupEvents(companyId, quoteIds) {
  if (!quoteIds.length) return [];
  // Firestore `in` filter caps at 10 values
  const batches = [];
  for (let i = 0; i < quoteIds.length; i += 10) {
    batches.push(quoteIds.slice(i, i + 10));
  }
  const events = [];
  for (const batch of batches) {
    try {
      const snap = await getDocs(query(
        collection(db, 'scheduled_actions'),
        where('kind', '==', 'quote_followup'),
        where('entityId', 'in', batch),
      ));
      snap.forEach((d) => {
        const data = d.data() || {};
        const stepLabel = `Step ${(Number(data.stepIndex) || 0) + 1}`;
        const status = (data.status || '').toLowerCase();
        const message = data.playbookActions?.[0]?.config?.message || '';
        const when = safeDate(data.executedAt) || safeDate(data.executeAt) || safeDate(data.createdAt);
        if (!when) return;

        if (status === 'executed' || status === 'partially_executed') {
          events.push({
            id: `f-${d.id}`,
            kind: 'followup_sent',
            when,
            title: `Auto follow-up · ${stepLabel}`,
            detail: message ? message.slice(0, 140) : 'Outbound SMS sent',
          });
        } else if (status === 'skipped_quote_approved') {
          events.push({
            id: `f-skip-${d.id}`,
            kind: 'followup_skipped',
            when,
            title: `Follow-up ${stepLabel} skipped`,
            detail: 'Quote already approved — automation stood down',
          });
        }
      });
    } catch {
      // ignore — older tenants may not have any scheduled_actions
    }
  }
  return events;
}

async function loadReplyEvents(companyId, leadId) {
  // Find a conversation anchored to this lead
  let conversationId = null;
  try {
    let snap = await getDocs(query(
      collection(db, 'companies', companyId, 'conversations'),
      where('anchor.id', '==', leadId),
      limit(1),
    ));
    if (snap.empty) {
      snap = await getDocs(query(
        collection(db, 'companies', companyId, 'conversations'),
        where('anchorId', '==', leadId),
        limit(1),
      ));
    }
    if (!snap.empty) conversationId = snap.docs[0].id;
  } catch { /* ignore */ }
  if (!conversationId) return [];

  const events = [];
  try {
    const snap = await getDocs(query(
      collection(db, 'companies', companyId, 'conversations', conversationId, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(40),
    ));
    snap.forEach((d) => {
      const data = d.data() || {};
      const direction = (data.direction || data.from || '').toString().toLowerCase();
      const isInbound = direction === 'inbound' || direction === 'client' || direction === 'customer';
      if (!isInbound) return;
      const when = safeDate(data.timestamp) || safeDate(data.createdAt);
      const body = (data.body || data.text || '').toString();
      if (!when) return;
      events.push({
        id: `r-${d.id}`,
        kind: 'client_reply',
        when,
        title: 'Customer replied',
        detail: body ? body.slice(0, 140) : '(SMS reply)',
      });
    });
  } catch { /* ignore */ }
  return events;
}

async function loadJobEvents(companyId, leadId) {
  const events = [];
  try {
    const snap = await getDocs(query(
      collection(db, 'companies', companyId, 'tasks'),
      where('leadId', '==', leadId),
      where('type', '==', 'job'),
      limit(40),
    ));
    snap.forEach((d) => {
      const data = d.data() || {};
      const scheduled = safeDate(data.scheduledStart) || safeDate(data.createdAt);
      if (scheduled) {
        events.push({
          id: `j-sched-${d.id}`,
          kind: 'job_scheduled',
          when: scheduled,
          title: data.title || 'Job scheduled',
          detail: data.description || '',
        });
      }
      if ((data.status || '').toLowerCase() === 'completed') {
        const completed = safeDate(data.completedAt) || safeDate(data.updatedAt);
        if (completed) {
          events.push({
            id: `j-done-${d.id}`,
            kind: 'job_completed',
            when: completed,
            title: 'Job completed — revenue captured',
            detail: data.title || '',
            won: true,
          });
        }
      }

      // ── Sprint 1: surface tasks[].history[] reschedule events ─────────
      // The canonical rescheduleTask helper appends one entry per change.
      // The array is bounded to 20 by the writer; we still slice defensively.
      const history = Array.isArray(data.history) ? data.history.slice(-20) : [];
      history.forEach((h, idx) => {
        if (!h || (h.type !== 'rescheduled' && h.type !== 'unscheduled')) return;
        const when = safeDate(h.at);
        if (!when) return;
        const isUn = h.type === 'unscheduled';
        const reasonLabel = RESCHEDULE_REASON_LABEL[h.reason] || h.reason || null;
        const detailLine = isUn
          ? `${formatReschedDate(h.fromStart)} → unscheduled`
          : `${formatReschedDate(h.fromStart)} → ${formatReschedDate(h.toStart)}`;
        const reasonChunk = reasonLabel ? ` · ${reasonLabel}` : '';
        const noteChunk   = h.note ? ` — ${h.note}` : '';
        events.push({
          id: `j-hist-${d.id}-${idx}`,
          kind: isUn ? 'job_unscheduled' : 'job_rescheduled',
          when,
          title: isUn ? 'Visit removed from calendar' : 'Visit rescheduled',
          detail: `${detailLine}${reasonChunk}${noteChunk}`,
          actor: h.byName || null,
        });
      });
    });
  } catch { /* ignore */ }
  return events;
}

// ── Component ────────────────────────────────────────────────────────────────

// Synthesizes program state-change events from the in-prop lead doc's
// `recurrence.history` array (cap-50, written by ServicePlanCard on toggle).
// No Firestore read — the lead doc is already loaded by the profile.
function loadProgramEvents(client) {
  if (!client?.recurrence?.history) return [];
  const history = Array.isArray(client.recurrence.history) ? client.recurrence.history : [];
  return history
    .map((h, idx) => {
      if (!h?.type) return null;
      const when = safeDate(h.at);
      if (!when) return null;
      const kind = h.type === 'program_marked_active'
        ? 'program_marked_active'
        : h.type === 'program_marked_inactive'
          ? 'program_marked_inactive'
          : null;
      if (!kind) return null;
      return {
        id: `pgm-${idx}-${h.at}`,
        kind,
        when,
        title: kind === 'program_marked_active'
          ? 'Recurring program marked Active'
          : 'Recurring program marked Inactive',
        detail: kind === 'program_marked_active'
          ? 'Future visits will be generated on completion.'
          : 'Future recurrence generation stopped. Scheduled visits remain on the calendar unless cancelled separately.',
        actor: h.byName || null,
      };
    })
    .filter(Boolean);
}

// How many timeline events render before the user pages older history into view.
// Tuned to comfortably fill the scrollable container without forcing a load on
// modest histories, while keeping render cost bounded for very large ones.
const INITIAL_VISIBLE = 20;
const PAGE_SIZE = 20;

const ActivityTimelineTab = ({ client }) => {
  const leadId = client?.id;
  const { companyId } = useCompany();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    let cancelled = false;
    if (!companyId || !leadId) { setLoading(false); return undefined; }

    setLoading(true);
    setVisibleCount(INITIAL_VISIBLE);
    (async () => {
      const { events: qEvents, quoteIds } = await loadQuoteEvents(companyId, leadId).catch(() => ({ events: [], quoteIds: [] }));
      const [fEvents, rEvents, jEvents] = await Promise.all([
        loadFollowupEvents(companyId, quoteIds).catch(() => []),
        loadReplyEvents(companyId, leadId).catch(() => []),
        loadJobEvents(companyId, leadId).catch(() => []),
      ]);
      const pEvents = loadProgramEvents(client);
      const merged = [...qEvents, ...fEvents, ...rEvents, ...jEvents, ...pEvents]
        .filter((e) => e.when)
        .sort((a, b) => b.when.getTime() - a.when.getTime());

      if (!cancelled) {
        setEvents(merged);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, leadId, client?.recurrence?.history]);

  // ── Conversion summary ────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const followups = events.filter((e) => e.kind === 'followup_sent').length;
    const skipped   = events.filter((e) => e.kind === 'followup_skipped').length;
    const won       = events.some((e) => e.won === true);

    // Did automation likely help? Heuristic: customer reply or approval came
    // AFTER at least one auto follow-up was sent.
    let automationLikelyHelped = false;
    if (followups > 0) {
      const lastFollowup = events.filter((e) => e.kind === 'followup_sent')
        .sort((a, b) => a.when - b.when).at(-1);
      const replyOrWinAfter = events.find(
        (e) =>
          (e.kind === 'client_reply' || e.kind === 'quote_approved') &&
          lastFollowup && e.when.getTime() > lastFollowup.when.getTime(),
      );
      automationLikelyHelped = !!replyOrWinAfter;
    }
    return { followups, skipped, won, automationLikelyHelped };
  }, [events]);

  // Slice for incremental rendering; keeps DOM bounded on long histories.
  const visibleEvents = useMemo(
    () => events.slice(0, visibleCount),
    [events, visibleCount],
  );
  const hasMore = events.length > visibleCount;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      data-testid="lead-activity-timeline"
    >
      <header className="flex items-center justify-between mb-5 gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Activity Timeline</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Chronological view — automation, replies, and revenue moments.
          </p>
        </div>
        {summary.followups > 0 && (
          <div className="flex items-center gap-1.5 text-[11px] font-medium" data-testid="timeline-summary">
            {summary.automationLikelyHelped && (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-100"
                data-testid="timeline-summary-helped"
              >
                <Sparkles className="w-3 h-3" />
                Automation reached customer
              </span>
            )}
            {summary.won && (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
                data-testid="timeline-summary-won"
              >
                <Trophy className="w-3 h-3" />
                Deal won
              </span>
            )}
          </div>
        )}
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
          <Loader2 className="w-4 h-4 animate-spin" />
          Building timeline…
        </div>
      ) : events.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 flex items-center gap-2" data-testid="timeline-empty">
          <Clock className="w-4 h-4" />
          No activity yet — events will appear here as quotes are sent and the
          customer engages.
        </div>
      ) : (
        <>
          <div
            className="max-h-[60vh] sm:max-h-[28rem] overflow-y-auto overscroll-contain pr-2 -mr-2"
            data-testid="timeline-scroll-container"
          >
            <ol className="relative border-l border-gray-200 ml-3 space-y-5">
              {visibleEvents.map((evt) => {
                const cfg = KIND[evt.kind] || { icon: Clock, tone: 'gray', label: 'Event', actor: '' };
                const tone = TONE_CLASSES[cfg.tone] || TONE_CLASSES.gray;
                const Icon = cfg.icon;
                return (
                  <li
                    key={evt.id}
                    className="relative pl-8"
                    data-testid={`timeline-event-${evt.kind}`}
                  >
                    <span
                      className={`absolute -left-[13px] top-0 w-6 h-6 rounded-full ring-2 ring-white ${tone.bg} flex items-center justify-center`}
                      aria-hidden
                    >
                      <Icon className={`w-3.5 h-3.5 ${tone.text}`} />
                    </span>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className={`text-[11px] font-semibold uppercase tracking-wide ${tone.text}`}>
                        {cfg.label}
                      </span>
                      {(evt.actor || cfg.actor) && (
                        <span className="text-[10px] text-gray-400">· {evt.actor || cfg.actor}</span>
                      )}
                      <span className="text-[11px] text-gray-400 ml-auto whitespace-nowrap">
                        {formatTime(evt.when)}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{evt.title}</p>
                    {evt.detail && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 break-words">
                        {evt.detail}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-gray-500">
            <span data-testid="timeline-count">
              Showing {visibleEvents.length} of {events.length}
            </span>
            {hasMore && (
              <button
                type="button"
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-50 hover:bg-gray-100 text-gray-700 ring-1 ring-gray-200 transition-colors"
                data-testid="timeline-load-older"
              >
                <ChevronDown className="w-3 h-3" />
                Load older activity
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ActivityTimelineTab;
